import type { InteractiveMode } from '@/types';
import { cn } from '@/lib/utils';
import { ConfidenceBadge } from './ConfidenceBadge';
import { AlertTriangle, Lock } from 'lucide-react';

interface InteractiveOptionsProps {
  interactive: InteractiveMode;
  onSelect: (value: string) => void;
  disabled?: boolean;
}

const iconMap: Record<string, string> = {
  plan: '📋',
  quick: '⚡',
  manual: '📖',
  product: '📦',
  confirm: '✓',
  cancel: '✗',
};

const methodColors: Record<string, string> = {
  POST: 'bg-green-500/20 text-green-600 dark:text-green-400',
  PUT: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
  PATCH: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
  DELETE: 'bg-red-500/20 text-red-600 dark:text-red-400',
};

function PlannedActionsList({ actions }: { actions: NonNullable<InteractiveMode['plannedActions']> }) {
  return (
    <div className="mb-3 rounded-lg border border-border bg-secondary/30 p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Actions to execute
      </div>
      <div className="space-y-2">
        {actions.map((action) => (
          <div key={action.order} className="flex items-start gap-2 text-sm">
            <span className="flex-shrink-0 text-muted-foreground">{action.order}.</span>
            <span className={cn(
              'flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-mono font-medium',
              methodColors[action.method] || 'bg-secondary text-foreground'
            )}>
              {action.method}
            </span>
            <span className="font-mono text-xs text-foreground">{action.path}</span>
          </div>
        ))}
      </div>
      {actions.length > 0 && actions[0]?.params && Object.keys(actions[0].params).length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            Show parameters
          </summary>
          <div className="mt-1 space-y-2">
            {actions.map((action) => (
              action.params && Object.keys(action.params).length > 0 && (
                <div key={`params-${action.order}`} className="rounded bg-secondary/50 p-2">
                  <div className="mb-1 text-xs text-muted-foreground">
                    {action.method} {action.path}
                  </div>
                  <pre className="text-xs text-foreground overflow-x-auto">
                    {JSON.stringify(action.params, null, 2)}
                  </pre>
                </div>
              )
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function GroundingWarning({ confidence }: { confidence: NonNullable<InteractiveMode['confidence']> }) {
  if (confidence.level === 'high') {
    return (
      <div className="mb-3">
        <ConfidenceBadge score={confidence.score} rationale={confidence.rationale} />
      </div>
    );
  }

  if (confidence.level === 'low') {
    return (
      <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <div className="text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-red-600 dark:text-red-400">Low confidence</span>
              <ConfidenceBadge score={confidence.score} rationale={confidence.rationale} />
            </div>
            <p className="mt-1 text-muted-foreground">{confidence.rationale}</p>
            {confidence.unsupportedActions && confidence.unsupportedActions.length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                {confidence.unsupportedActions.map((action, i) => (
                  <li key={i}>{action}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Medium confidence
  return (
    <div className="mb-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-500" />
        <div className="text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-yellow-600 dark:text-yellow-400">Partially verified</span>
            <ConfidenceBadge score={confidence.score} rationale={confidence.rationale} />
          </div>
          {confidence.unsupportedActions && confidence.unsupportedActions.length > 0 && (
            <div className="mt-1">
              <p className="text-muted-foreground">These actions could not be verified:</p>
              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                {confidence.unsupportedActions.map((action, i) => (
                  <li key={i}>{action}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MissingScopesWarning({ scopes }: { scopes: string[]; reconnectUrl?: string }) {
  return (
    <div className="mb-3 space-y-2 rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-3">
      <div className="flex items-start gap-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
        <p className="text-sm text-yellow-400/80 break-words min-w-0">
          Requires {scopes.map((s, i) => (
            <span key={s}>
              {i > 0 && ', '}
              <code className="rounded bg-yellow-500/10 px-1 py-0.5 text-yellow-400 break-all">{s}</code>
            </span>
          ))} scope{scopes.length > 1 ? 's' : ''}
        </p>
      </div>
      <p className="text-xs text-yellow-400/60 pl-7">
        Add the scope{scopes.length > 1 ? 's' : ''} in{' '}
        <a href="https://marketplace.zoom.us/user/build" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-400">
          Zoom Marketplace
        </a>
        , then click <strong className="text-yellow-400/80">Reconnect Zoom</strong> to re-authorize.
      </p>
    </div>
  );
}

export function InteractiveOptions({ interactive, onSelect, disabled }: InteractiveOptionsProps) {
  if (interactive.type !== 'choice' || !interactive.options?.length) {
    return null;
  }

  return (
    <div className="mt-4">
      {interactive.plannedActions && interactive.plannedActions.length > 0 && (
        <PlannedActionsList actions={interactive.plannedActions} />
      )}
      {interactive.missingScopes && interactive.missingScopes.length > 0 && (
        <MissingScopesWarning scopes={interactive.missingScopes} reconnectUrl={interactive.reconnectUrl} />
      )}
      {interactive.confidence && (
        <GroundingWarning confidence={interactive.confidence} />
      )}
      <div className="flex flex-wrap gap-2">
        {interactive.options.map((option) => (
          <button
            key={option.id}
            onClick={() => {
              if (option.url) {
                window.open(option.url, 'zac-zoom-oauth', 'popup,width=600,height=700');
              } else {
                onSelect(option.value);
              }
            }}
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg',
              'bg-primary/10 hover:bg-primary/20 border border-primary/20',
              'text-sm font-medium text-foreground',
              'transition-colors duration-150',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus:outline-none focus:ring-2 focus:ring-primary/50'
            )}
          >
            {option.icon && (
              <span className="text-base">{iconMap[option.icon] || '•'}</span>
            )}
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
